import { Component, Input, OnInit, SimpleChanges, ViewEncapsulation } from '@angular/core';

import { Observable } from 'rxjs';

import { Antwoord, Controltype, ValidatieParameter, Vraag } from '@models/claims';
import { IBaseVraagboomResultaat, Meetgegevens, VraagboomAntwoord, VraagboomDateResultaat } from '@models/vragenboom';
import { FormatHelper, ValidatieregelHelper } from '@services/public_api';
import { BaseVraagControlDirective } from '../base/base-vraag-control';
import { DatumValidator } from '../../../utils/validation/datum.validator';
import { DataService } from '../../../services/data.service';
import { MetenMapper } from '../../../mappers/meten.mapper';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  providers: [],
  encapsulation: ViewEncapsulation.None,
})
export class DatePickerComponent extends BaseVraagControlDirective<VraagboomDateResultaat> implements OnInit {
  @Input() isCurrentVraag: boolean;

  placeholder = 'dd-mm-jjjj';
  validatieError = false;
  errormessageValidatie: string;
  errormessageText: string;
  textHadFocus = false;
  calOpen = false;
  calCloseOnSelect = false;
  showPrevNext = false;
  meetId: string;
  showTextbox = false;
  textboxEvent = false;
  validDate = false;
  resultaten$: Observable<VraagboomAntwoord<IBaseVraagboomResultaat>[]>;
  resultaten: VraagboomAntwoord<IBaseVraagboomResultaat>[];
  meetgegevens: Meetgegevens;

  _tekst: string;
  get tekst(): string {
    return this._tekst;
  }
  set tekst(val: string) {
    this._tekst = val;
  }

  minDatum = null;
  maxDatum = new Date(2099, 0, 1);
  minDatumAanwezig = false;
  maxDatumAanwezig = false;
  _datum: Date;
  get datum(): Date {
    return this._datum;
  }
  set datum(val: Date) {
    this._datum = val;
    if (!this.textboxEvent) {
      this.validDate = true;
      this._tekst = FormatHelper.formatDate(this._datum, "dd-MM-yyyy");
    }
    this.notifyAntwoord(this.datum);
  }

  constructor(_dataService: DataService) {
    super(_dataService);
  }

  ngOnInit() { }

  initialize(changes: SimpleChanges): void {
    if (!this.isCurrentVraag) { return; }

    this.meetgegevens = this._dataService.getMeetgegevens();
    this.meetId = this._dataService.getMeetidPazaProces() + '-' + this.meetgegevens.status + this.meetgegevens.sleutel +
      MetenMapper.geefVolgnr(this.vraag, this.meetgegevens.reeks) + '_' + MetenMapper.format(this.vraag.titel);

    this.resultaten$ = this._dataService.resultaten;
    this.resultaten$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(res => {
        if (res !== undefined && res !== null) {
          this.resultaten = res;
          this.verwerkResultaat();
        }
      });
  }

  verwerkResultaat(): void {
    let kleinerGelijk, groterGelijk;

    if (this.vraag?.antwoorden?.length > 0) {
      kleinerGelijk = this.vraag.antwoorden[0].validatieParameters.find(v => v.type_validatie === 'DATUM_KLEINER_GELIJK');
      if (kleinerGelijk != null) {
        this.maxDatum = DatumValidator.getComparisonDate(kleinerGelijk.validatie_parameters, this.resultaten);
        this.maxDatumAanwezig = true;
      }

      groterGelijk = this.vraag.antwoorden[0].validatieParameters.find(v => v.type_validatie === 'DATUM_GROTER_GELIJK');
      if (groterGelijk != null) {
        this.minDatum = DatumValidator.getComparisonDate(groterGelijk.validatie_parameters, this.resultaten);
        this.minDatumAanwezig = true;
      }
    }

    if (this.vraag.control === Controltype.Kalender) {
      this.calOpen = true;
      this.showPrevNext = true;
    }
    if (this.vraag.control === Controltype.KalenderDmj) {
      this.calOpen = false;
      this.showTextbox = true;
      this.calCloseOnSelect = true;

      if (this.vraag?.antwoorden?.length > 0) {
        this.errormessageText = this.vraag.antwoorden[0].foutmelding;
      }
    }

    if (this.antwoordResultaat != null && this.antwoordResultaat.resultaat != null) {
      this.datum = this.antwoordResultaat.resultaat.value;
      if (this.vraag.control === Controltype.KalenderDmj) {
        this._tekst = FormatHelper.formatDate(this._datum, "dd-MM-yyyy");
        this.validDate = true;
      }
    } else {
      const now = new Date();
      if (kleinerGelijk && this.maxDatum && now > this.maxDatum) {
        this.maxDatum.setHours(0, 0, 0);
        this.datum = this.maxDatum;
      } else {
        now.setHours(0, 0, 0, 0);
        this.datum = now;
      }
      if (this.vraag.control === Controltype.KalenderDmj) {
        this._tekst = '';
      }
    }
  }

  notifyAntwoord(datum: Date) {
    const antwoorden: Antwoord[] = [];
    if (this.vraag?.antwoorden?.length > 0) {
      antwoorden.push(this.vraag.antwoorden[0]);
    }
    this.setAntwoord(antwoorden, new VraagboomDateResultaat(datum));
  }

  isTextLeeg(): boolean {
    return (this._tekst == null) || (this._tekst.length === 0);
  }

  geefDatum(m: RegExpMatchArray): Date {
    var d = new Date();
    d.setHours(6, 0, 0, 0);
    d.setFullYear(Number(m[3]));
    d.setDate(1); // tijdelijk naar 1, om problemen met 31
    d.setMonth(Number(m[2]) - 1);
    d.setDate(Number(m[1]));
    return d;
  }

  checkDatum(m: RegExpMatchArray): void {
    if (m && m.length === 4) {
      // controleer datum rekening houdend met voorloopnullen
      var d = this.geefDatum(m);

      // controleer of de invoer gelijk is aan de datum, 56-1-2020 wordt gezien als een valide datum
      if (FormatHelper.pad(d.getDate()) === FormatHelper.pad(Number(m[1]))
        && FormatHelper.pad(d.getMonth() + 1) === FormatHelper.pad(Number(m[2]))
        && d.getFullYear() === Number(m[3])) {
        this.validDate = true;
        d.setHours(0, 0, 0);
        this.datum = d;
      } else {
        this.validDate = false;
        this.notifyAntwoord(new Date(3000, 1, 1));
      }
    } else {
      this.validDate = false;
      this.notifyAntwoord(new Date(3000, 1, 1));
    }
  }

  findDatumMatch(): RegExpMatchArray {
    return this.tekst.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  }

  onKey(event: KeyboardEvent) {
    this.textboxEvent = true;
    this._tekst = (event.target as HTMLInputElement).value;
    if (this._tekst?.length > 0) {
      this.checkDatum(this.findDatumMatch());
    } else {
      this.validDate = false;
    }
    this.textboxEvent = false;
  }

  setTextFocus(focus: boolean) {
    this.textboxEvent = true;
    if (focus) {
      this.calOpen = false;
    } else {
      this.textHadFocus = true;
      if (this._tekst?.length > 0) {
        this.checkDatum(this.findDatumMatch());
      } else {
        this.notifyAntwoord(new Date(3000, 1, 1));
      }
    }
    this.textboxEvent = false;
  }

  toggleCalendar(): void {
    this.calOpen = !this.calOpen;
    if (!this.tekst || this.tekst.length === 0) {
      this.datum = this.maxDatumAanwezig ? this.maxDatum : this.minDatumAanwezig ? this.minDatum : new Date();
    }
  }

  validate(vraag: Vraag, antwoorden: Antwoord[], resultaat: VraagboomDateResultaat): boolean {
    if (this.vraag.control === Controltype.KalenderDmj && (this.isTextLeeg() || !this.validDate)) {
      return false;
    }
    this.validatieError = false;
    if (!DatumValidator.isSmallerThan(antwoorden[0], resultaat, this.resultaten)) {
      this.validatieError = true;
      const validatie: ValidatieParameter = ValidatieregelHelper.get(antwoorden[0].validatieParameters, 'DATUM_KLEINER_GELIJK');
      this.errormessageValidatie = 'Kies een datum op of voor '
        + FormatHelper.formatDate(DatumValidator.getComparisonDate(validatie.validatie_parameters, this.resultaten), 'dd-MM-yyyy');
    } else if (!DatumValidator.isGreaterThan(antwoorden[0], resultaat, this.resultaten)) {
      this.validatieError = true;
      const validatie: ValidatieParameter = ValidatieregelHelper.get(antwoorden[0].validatieParameters, 'DATUM_GROTER_GELIJK');
      this.errormessageValidatie = 'Kies een datum op of na '
        + FormatHelper.formatDate(DatumValidator.getComparisonDate(validatie.validatie_parameters, this.resultaten), 'dd-MM-yyyy');
    }
    return (antwoorden != null && antwoorden.length === 1) &&
      DatumValidator.isValidDate(antwoorden[0], resultaat) &&
      DatumValidator.isSmallerThan(antwoorden[0], resultaat, this.resultaten) &&
      DatumValidator.isGreaterThan(antwoorden[0], resultaat, this.resultaten);
  }
}
